import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Switch } from '@/shared/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/components/ui/dialog';
import { toast } from 'sonner';
import {
  Layers, Plus, GripVertical, Trash2, Edit, Eye, EyeOff,
  ChevronDown, ChevronUp, Settings, ArrowLeft, Link2,
  ExternalLink, MonitorPlay, X, Check, AlertCircle, Rocket, Zap
} from 'lucide-react';
import useLandingConfig from '../hooks/useLandingConfig';
import { API_BASE } from '@/shared/services/api';

const API_URL = API_BASE;

const SECTION_TYPES = [
  { value: 'hero',         label: 'Hero Section' },
  { value: 'features',     label: 'Features' },
  { value: 'modules',      label: 'Modules Showcase' },
  { value: 'pricing',      label: 'Pricing Plans' },
  { value: 'testimonials', label: 'Testimonials' },
  { value: 'faq',          label: 'FAQ' },
  { value: 'cta',          label: 'Call to Action' },
  { value: 'stats',        label: 'Statistics' },
  { value: 'team',         label: 'Team' },
  { value: 'partners',     label: 'Partners/Logos' },
  { value: 'footer',       label: 'Footer' },
  { value: 'custom',       label: 'Custom Section' },
];

const BACKGROUND_TYPES = [
  { value: 'solid',       label: 'Solid Color' },
  { value: 'gradient',    label: 'Gradient' },
  { value: 'image',       label: 'Background Image' },
  { value: 'transparent', label: 'Transparent' },
];

// ── Presentation Picker Hook ──────────────────────────────────────────────────
function usePresentations() {
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading]             = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/landing/presentations/available`)
      .then(r => r.json())
      .then(d => setPresentations(d.presentations || []))
      .catch(() => setPresentations([]))
      .finally(() => setLoading(false));
  }, []);

  return { presentations, loading };
}

// ── Block Editor Dialog ───────────────────────────────────────────────────────
function BlockEditorDialog({ block, sectionKey, onSave, onPublish, onClose }) {
  const { presentations, loading: presLoading } = usePresentations();

  const [ctaText,        setCtaText]        = useState(block.cta_text  || '');
  const [ctaUrl,         setCtaUrl]         = useState(block.cta_url   || '');
  const [selectedPres,   setSelectedPres]   = useState('');
  const [saving,         setSaving]         = useState(false);

  // On open: find if current cta_url matches a known presentation
  useEffect(() => {
    if (!presentations.length) return;
    const match = presentations.find(p => p.publicPath === block.cta_url);
    setSelectedPres(match ? match.id : '__custom__');
  }, [presentations, block.cta_url]);

  const handlePresSelect = (presId) => {
    setSelectedPres(presId);
    if (presId === '__none__') {
      setCtaUrl('');
    } else if (presId !== '__custom__') {
      const pres = presentations.find(p => p.id === presId);
      if (pres) setCtaUrl(pres.publicPath);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await onSave(block.id, {
      cta_text: ctaText || block.cta_text,
      cta_url:  ctaUrl,
    });
    if (result.success) {
      // Auto-publish so the public landing page snapshot is immediately updated
      const pubResult = await onPublish();
      setSaving(false);
      if (pubResult.success) {
        toast.success(`✅ "${block.title || block.block_key}" saved and published live!`);
      } else {
        toast.success(`Block saved — but publish failed: ${pubResult.error || 'unknown error'}. Click Publish to retry.`);
      }
      onClose();
    } else {
      setSaving(false);
      toast.error(result.error || 'Failed to save block');
    }
  };

  const selectedPresInfo = presentations.find(p => p.id === selectedPres);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-blue-400" />
            Edit Block: <span className="text-blue-400">{block.title || block.block_key}</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Section: <span className="font-mono text-slate-300">{sectionKey}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">

          {/* CTA Label */}
          <div className="space-y-2">
            <Label className="text-white text-sm font-semibold">CTA Button Label</Label>
            <Input
              value={ctaText}
              onChange={e => setCtaText(e.target.value)}
              placeholder={block.cta_text || 'e.g. Explore Fiscal'}
              className="bg-slate-800 border-slate-600 text-white"
            />
          </div>

          {/* Presentation Picker */}
          <div className="space-y-2">
            <Label className="text-white text-sm font-semibold flex items-center gap-2">
              <MonitorPlay className="w-4 h-4 text-blue-400" />
              Link to Presentation
            </Label>
            <Select value={selectedPres} onValueChange={handlePresSelect} disabled={presLoading}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder={presLoading ? "Loading presentations…" : "Choose a presentation deck…"} />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 max-h-64">
                <SelectItem value="__none__"    className="text-slate-400 italic">— No presentation (hide button) —</SelectItem>
                <SelectItem value="__custom__"  className="text-slate-400 italic">— Custom URL (type below) —</SelectItem>
                <div className="border-t border-slate-700 my-1" />
                {presentations.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-white">
                    <div className="flex flex-col">
                      <span className="font-medium">{p.name}</span>
                      <span className="text-xs text-slate-400">{p.audience}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Show resolved URL preview or custom input */}
            {selectedPres === '__custom__' ? (
              <div className="space-y-1">
                <Input
                  value={ctaUrl}
                  onChange={e => setCtaUrl(e.target.value)}
                  placeholder="/custom-path or https://..."
                  className="bg-slate-800 border-slate-600 text-white font-mono text-sm"
                />
                <p className="text-xs text-slate-500">Enter any path or URL for the CTA button.</p>
              </div>
            ) : selectedPresInfo ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-blue-300 font-medium truncate">{selectedPresInfo.name}</p>
                  <p className="text-xs text-slate-400 font-mono truncate">{selectedPresInfo.publicPath}</p>
                </div>
                <a
                  href={selectedPresInfo.publicPath}
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-400 hover:text-blue-400 transition-colors flex-shrink-0"
                  title="Preview presentation"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            ) : selectedPres === '__none__' ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <p className="text-xs text-amber-300">The CTA button will be hidden for this block.</p>
              </div>
            ) : null}
          </div>

          {/* Languages badge row (informational) */}
          {selectedPresInfo && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Languages:</span>
              {selectedPresInfo.languages.map(l => (
                <Badge key={l} variant="outline" className="text-xs text-slate-300 border-slate-600 uppercase">{l}</Badge>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white">
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Section Editor ───────────────────────────────────────────────────────
export const SectionEditor = () => {
  const { t } = useTranslation("landing");
  const {
    sections, loading,
    createSection, updateSection, deleteSection,
    reorderSections, updateContentBlock, publish,
  } = useLandingConfig();

  const [isCreateOpen,   setIsCreateOpen]   = useState(false);
  const [editingBlock,   setEditingBlock]   = useState(null);   // { block, sectionKey }
  const [expandedSec,    setExpandedSec]    = useState(null);   // section.id
  const [unpublished,    setUnpublished]    = useState(false);  // show publish banner
  const [publishing,     setPublishing]     = useState(false);

  const [newSection, setNewSection] = useState({
    section_key: '', section_type: 'custom', is_visible: true,
    background_type: 'solid', background_value: '', config: {}
  });

  const handleCreate = async () => {
    if (!newSection.section_key) { toast.error('Section key is required'); return; }
    const result = await createSection({ ...newSection, order_index: sections.length });
    if (result.success) {
      toast.success('Section created!');
      setIsCreateOpen(false);
      setNewSection({ section_key: '', section_type: 'custom', is_visible: true, background_type: 'solid', background_value: '', config: {} });
    } else {
      toast.error(result.error || 'Failed to create section');
    }
  };

  const handleUpdate = async (sectionId, updates) => {
    const result = await updateSection(sectionId, updates);
    if (result.success) toast.success('Section updated!');
    else toast.error(result.error || 'Failed to update section');
  };

  const handleDelete = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    const result = await deleteSection(sectionId);
    if (result.success) toast.success('Section deleted!');
    else toast.error(result.error || 'Failed to delete section');
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newOrder = sections.map((s, i) => ({ id: s.id, order_index: i === index ? i - 1 : i === index - 1 ? i + 1 : i }));
    await reorderSections(newOrder);
  };

  const handleMoveDown = async (index) => {
    if (index === sections.length - 1) return;
    const newOrder = sections.map((s, i) => ({ id: s.id, order_index: i === index ? i + 1 : i === index + 1 ? i - 1 : i }));
    await reorderSections(newOrder);
  };

  const toggleVisibility = async (sectionId, currentVisibility) => {
    await handleUpdate(sectionId, { is_visible: !currentVisibility });
  };

  const handleBlockSave = async (blockId, updates) => {
    return await updateContentBlock(blockId, updates);
  };

  const handlePublish = async () => {
    setPublishing(true);
    const label = `v-links-${Date.now()}`;
    const result = await publish(label, 'Auto-published from Section Editor — presentation link update');
    setPublishing(false);
    if (result.success) {
      setUnpublished(false);
      toast.success('🚀 Changes published! Landing page is now live.');
    } else {
      toast.error(result.error || 'Publish failed');
    }
    return result;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <a href="/admin/landing"><ArrowLeft className="w-5 h-5" /></a>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Layers className="w-6 h-6 text-blue-500" />
              {t("sectionEditor", "Section Editor")}
            </h1>
            <p className="text-slate-500 dark:text-slate-400">{t("manageAndReorder", "Manage sections and link module blocks to presentations")}</p>
          </div>
        </div>

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-section-btn">
              <Plus className="w-4 h-4" />{t("addSection", "Add Section")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">{t("createNewSection", "Create New Section")}</DialogTitle>
              <DialogDescription className="text-slate-400">{t("addANew", "Add a new section to your landing page")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-white">{t("sectionKey", "Section Key")}</Label>
                <Input
                  placeholder="e.g., custom_section"
                  value={newSection.section_key}
                  onChange={e => setNewSection({ ...newSection, section_key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="section-key-input"
                />
                <p className="text-xs text-slate-400">{t("uniqueIdentifierFor", "Unique identifier for this section (lowercase, no spaces)")}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-white">{t("sectionType", "Section Type")}</Label>
                <Select value={newSection.section_type} onValueChange={v => setNewSection({ ...newSection, section_type: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {SECTION_TYPES.map(type => <SelectItem key={type.value} value={type.value} className="text-white">{type.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white">{t("backgroundType", "Background Type")}</Label>
                <Select value={newSection.background_type} onValueChange={v => setNewSection({ ...newSection, background_type: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {BACKGROUND_TYPES.map(type => <SelectItem key={type.value} value={type.value} className="text-white">{type.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {newSection.background_type !== 'transparent' && (
                <div className="space-y-2">
                  <Label className="text-white">{t("backgroundValue", "Background Value")}</Label>
                  <Input
                    placeholder={newSection.background_type === 'solid' ? '#1e293b' : 'URL or gradient CSS'}
                    value={newSection.background_value}
                    onChange={e => setNewSection({ ...newSection, background_value: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label className="text-white">{t("visible", "Visible")}</Label>
                <Switch checked={newSection.is_visible} onCheckedChange={checked => setNewSection({ ...newSection, is_visible: checked })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>{t("cancel", "Cancel")}</Button>
              <Button onClick={handleCreate} data-testid="create-section-btn">{t("createSection", "Create Section")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Unpublished Changes Banner */}
      {unpublished && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/40">
          <Zap className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">You have unpublished changes</p>
            <p className="text-xs text-amber-600 dark:text-amber-400">The landing page still shows the old links. Publish to make them live.</p>
          </div>
          <button
            onClick={handlePublish}
            disabled={publishing}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-all shadow-sm disabled:opacity-60"
          >
            <Rocket className="w-3.5 h-3.5" />
            {publishing ? 'Publishing…' : 'Publish Now'}
          </button>
        </div>
      )}

      {/* Sections List */}
      <div className="space-y-3">
        {sections.length === 0 ? (
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <div className="py-12 text-center">
              <Layers className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-800 dark:text-white mb-2">{t("noSectionsYet", "No sections yet")}</h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">{t("addSectionsTo", "Add sections to build your landing page")}</p>
              <Button onClick={() => setIsCreateOpen(true)} className="gap-2">
                <Plus className="w-4 h-4" />{t("addYourFirst", "Add Your First Section")}
              </Button>
            </div>
          </Card>
        ) : (
          [...sections]
            .sort((a, b) => a.order_index - b.order_index)
            .map((section, index) => {
              const isExpanded = expandedSec === section.id;
              const blocks = section.content_blocks || [];
              const isModules = section.section_type === 'modules' || section.section_key === 'modules';

              return (
                <Card
                  key={section.id}
                  className={`bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 shadow-sm ${!section.is_visible ? 'opacity-60' : ''}`}
                  data-testid={`section-card-${section.section_key}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      {/* Order controls */}
                      <div className="flex flex-col items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMoveUp(index)} disabled={index === 0}>
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <GripVertical className="w-5 h-5 text-slate-500" />
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMoveDown(index)} disabled={index === sections.length - 1}>
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Section Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-semibold text-slate-900 dark:text-white capitalize">{section.section_key.replace(/_/g, ' ')}</h3>
                          <Badge variant="outline" className="text-xs">{section.section_type}</Badge>
                          <Badge variant={section.is_visible ? 'default' : 'secondary'} className="text-xs">
                            {section.is_visible ? 'Visible' : 'Hidden'}
                          </Badge>
                          {isModules && (
                            <Badge className="text-xs bg-blue-100 dark:bg-blue-600/20 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-600/40">
                              <Link2 className="w-3 h-3 mr-1" /> Presentation Links
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                          {blocks.length} content blocks • Background: {section.background_type}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        {/* Expand to see blocks — prominent pill button */}
                        {blocks.length > 0 && (
                          <button
                            onClick={() => setExpandedSec(isExpanded ? null : section.id)}
                            title={isExpanded ? 'Collapse blocks' : 'Edit blocks / presentation links'}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                              isExpanded
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-500/30 border border-blue-300 dark:border-blue-500/40'
                            }`}
                          >
                            <Settings className="w-3.5 h-3.5" />
                            {isExpanded ? 'Close' : 'Edit Links'}
                          </button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => toggleVisibility(section.id, section.is_visible)} title={section.is_visible ? 'Hide' : 'Show'}>
                          {section.is_visible ? <Eye className="w-4 h-4 text-green-500" /> : <EyeOff className="w-4 h-4 text-slate-500" />}
                        </Button>
                        <Button variant="ghost" size="icon" asChild>
                          <a href={`/admin/landing/sections/${section.id}`}><Edit className="w-4 h-4" /></a>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(section.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Blocks Panel */}
                    {isExpanded && blocks.length > 0 && (
                      <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-4 space-y-2">
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-3">
                          Content Blocks — click <Link2 className="inline w-3 h-3 mx-0.5" /> to link a presentation
                        </p>
                        {[...blocks].sort((a, b) => a.order_index - b.order_index).map(block => (
                          <div
                            key={block.id}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-slate-600 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 dark:text-white truncate">{block.title || block.block_key}</p>
                              {block.cta_url ? (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <Link2 className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                  <span className="text-xs font-mono text-blue-600 dark:text-blue-400 truncate">{block.cta_url}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400 dark:text-slate-500 italic">No presentation linked</span>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingBlock({ block, sectionKey: section.section_key })}
                              className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-300 gap-1.5 flex-shrink-0"
                            >
                              <Link2 className="w-3.5 h-3.5" />
                              {block.cta_url ? 'Change' : 'Link'}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
        )}
      </div>

      {/* Block Editor Dialog */}
      {editingBlock && (
        <BlockEditorDialog
          block={editingBlock.block}
          sectionKey={editingBlock.sectionKey}
          onSave={handleBlockSave}
          onPublish={handlePublish}
          onClose={() => setEditingBlock(null)}
        />
      )}
    </div>
  );
};

export default SectionEditor;